import type { Child, MedicalRequest } from "@spork/s4p-server-sdk";
import { useRouter } from "expo-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";

import { toastError, toastSuccess } from "@/components/AppToast";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/text";
import { Textarea } from "@/components/ui/textarea";
import { getChildLabel } from "@/features/selectedChild";
import { log } from "@/logger";
import {
    useCreateMedicalRequestMutation,
    useUpdateMedicalRequestMutation,
} from "@/query/apiSlice";
import { TEST_IDS } from "@/testIdDefinition";

type MedicalRequestFormProps = {
    child: Child;
    existingRequest?: MedicalRequest;
};

function getErrorMessage(error: unknown, fallback: string): string {
    if (
        error &&
        typeof error === "object" &&
        "message" in error &&
        typeof error.message === "string"
    ) {
        return error.message;
    }
    return fallback;
}

export function MedicalRequestForm({
    child,
    existingRequest,
}: MedicalRequestFormProps) {
    const { t } = useTranslation();
    const router = useRouter();
    const [createMedicalRequest, { isLoading: isCreating }] =
        useCreateMedicalRequestMutation();
    const [updateMedicalRequest, { isLoading: isUpdating }] =
        useUpdateMedicalRequestMutation();
    const [note, setNote] = useState(existingRequest?.note ?? "");

    const isEdit = Boolean(existingRequest);
    const isSubmitting = isCreating || isUpdating;
    const canSubmit = note.trim().length > 0;

    async function handleSubmit() {
        if (!canSubmit) {
            toastError(t("medical.errorTitle"), t("medical.noteRequired"));
            return;
        }

        try {
            if (existingRequest) {
                await updateMedicalRequest({
                    id: existingRequest.id,
                    note,
                }).unwrap();
                toastSuccess(
                    t("medical.updatedTitle"),
                    t("medical.updatedBody", { name: getChildLabel(child) }),
                );
            } else {
                await createMedicalRequest({
                    child_id: child.id,
                    note,
                }).unwrap();
                toastSuccess(
                    t("medical.submittedTitle"),
                    t("medical.submittedBody", { name: getChildLabel(child) }),
                );
            }

            if (router.canGoBack()) {
                router.back();
            } else {
                router.replace("/");
            }
        } catch (error) {
            log.error("medicalRequest submit failed", error);
            toastError(
                t("medical.errorTitle"),
                getErrorMessage(
                    error,
                    isEdit
                        ? t("medical.updateFailed")
                        : t("medical.submitFailed"),
                ),
            );
        }
    }

    return (
        <View className="gap-6">
            <View className="gap-2">
                <Text variant="muted">{t("medical.noteHint")}</Text>
                <Textarea
                    className="max-h-[240px] min-h-[120px]"
                    placeholder={t("medical.notePlaceholder")}
                    value={note}
                    onChangeText={setNote}
                    testID={TEST_IDS[TEST_IDS.MEDICAL_REQUEST_NOTE_FIELD]}
                />
            </View>
            <Button
                onPress={handleSubmit}
                disabled={isSubmitting || !canSubmit}
                testID={TEST_IDS[TEST_IDS.MEDICAL_REQUEST_SUBMIT_BUTTON]}
            >
                <Text className="text-primary-foreground">
                    {isSubmitting
                        ? isEdit
                            ? t("medical.saving")
                            : t("medical.submitting")
                        : isEdit
                          ? t("medical.save")
                          : t("medical.submit")}
                </Text>
            </Button>
        </View>
    );
}
